import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { SignupService, MeetingSlot, MeetingConfirmation } from '../../../tpiq-services/signup.service';
import { Session } from '../../../services/Session';

interface CalendarDay {
  date: Date;
  inCurrentMonth: boolean;
  isPrevMonth: boolean;
  hasSlots: boolean;
  isToday: boolean;
  isPast: boolean;
}


@Component({
  selector: 'app-meeting-scheduler',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './meeting-scheduler.component.html',
  styleUrls: ['./meeting-scheduler.component.scss'],
})
export class MeetingSchedulerComponent implements OnInit {
  @Output() booked = new EventEmitter<void>();

  // ── State ──────────────────────────────────────────────────────────────────
  view: 'calendar' | 'slots' | 'confirmed' = 'calendar';

  // ── Duration ───────────────────────────────────────────────────────────────
  durations: { label: string; ms: number }[] = [];
  selectedDuration = 0; // 0 = not yet known; set after probe

  private durationLabel(ms: number): string {
    const minutes = ms / 60_000;
    if (minutes < 60) return `${minutes} mins`;
    const hours = minutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  slots: MeetingSlot[] = [];
  slotsByDate = new Map<string, MeetingSlot[]>();

  calendarDays: CalendarDay[] = [];
  currentMonth = new Date();

  // Navigation bounds
  private readonly today = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();
  private readonly minMonthIndex = this.today.getFullYear() * 12 + this.today.getMonth();

  private get currentMonthIndex(): number {
    return this.currentMonth.getFullYear() * 12 + this.currentMonth.getMonth();
  }

  // Can go back only if we're not already on the current month
  get canGoPrev(): boolean { return this.currentMonthIndex > this.minMonthIndex; }

  // Can go forward only if HubSpot returned any slots in the next month
  get canGoNext(): boolean {
    const nextYear  = this.currentMonth.getMonth() === 11 ? this.currentMonth.getFullYear() + 1 : this.currentMonth.getFullYear();
    const nextMonth = (this.currentMonth.getMonth() + 1) % 12;
    return Array.from(this.slotsByDate.keys()).some(key => {
      // Parse "YYYY-MM-DD" directly to avoid UTC-vs-local ambiguity
      const [y, m] = key.split('-').map(Number);
      return y === nextYear && (m - 1) === nextMonth;
    });
  }

  selectedDate: Date | null = null;
  slotsForDate: MeetingSlot[] = [];
  selectedSlot: MeetingSlot | null = null;

  confirmation: MeetingConfirmation | null = null;

  loading  = false;
  booking  = false;
  error    = '';

  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // ── Timezone dropdown ───────────────────────────────────────────────────────
  tzDropdownOpen = false;
  tzSearch       = '';

  readonly TIMEZONE_GROUPS: { group: string; zones: string[] }[] = [
    { group: 'US', zones: [
      'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific',
      'US/Alaska', 'US/Hawaii', 'US/East-Indiana',
    ]},
    { group: 'Canada', zones: [
      'Canada/Pacific', 'Canada/Yukon', 'Canada/Mountain', 'Canada/Saskatchewan',
      'Canada/Central', 'Canada/Eastern', 'Canada/Atlantic', 'Canada/Newfoundland',
    ]},
    { group: 'America', zones: [
      'America/Adak', 'America/Anchorage', 'America/Creston', 'America/Ensenada',
      'America/Bahia_Banderas', 'America/Boise', 'America/Atikokan', 'America/Chicago',
      'America/Anguilla', 'America/Detroit', 'America/Havana', 'America/Araguaina',
      'America/Glace_Bay', 'America/Santiago', 'America/Godthab', 'America/Miquelon',
      'America/Noronha', 'America/St_Johns', 'America/Danmarkshavn',
    ]},
    { group: 'Europe', zones: [
      'Europe/Belfast', 'Europe/Amsterdam', 'Europe/Athens', 'Europe/Chisinau',
      'Europe/Kaliningrad', 'Europe/Istanbul', 'Europe/Astrakhan',
    ]},
    { group: 'Africa', zones: [
      'Africa/Abidjan', 'Africa/Algiers', 'Africa/Casablanca', 'Africa/Ceuta',
      'Africa/Blantyre', 'Africa/Cairo', 'Africa/Addis_Ababa',
    ]},
    { group: 'Asia', zones: [
      'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka',
      'Asia/Bangkok', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
    ]},
    { group: 'Australia', zones: [
      'Australia/Perth', 'Australia/Adelaide', 'Australia/Sydney',
    ]},
    { group: 'Pacific', zones: [
      'Pacific/Honolulu', 'Pacific/Auckland',
    ]},
    { group: 'Antarctica', zones: [
      'Antarctica/Palmer', 'Antarctica/Troll', 'Antarctica/Syowa', 'Antarctica/Mawson',
      'Antarctica/Davis', 'Antarctica/Casey', 'Antarctica/DumontDUrville',
      'Antarctica/Macquarie', 'Antarctica/McMurdo',
    ]},
  ];

  tzDisplay(iana: string): string {
    const date = new Date();
    const rawOffset = new Intl.DateTimeFormat('en-US', { timeZone: iana, timeZoneName: 'longOffset' })
      .formatToParts(date).find(p => p.type === 'timeZoneName')?.value ?? 'GMT';
    const offset = rawOffset.replace('GMT+', 'UTC +').replace('GMT-', 'UTC -').replace(/^GMT$/, 'UTC +00:00');
    const name = new Intl.DateTimeFormat('en-US', { timeZone: iana, timeZoneName: 'long' })
      .formatToParts(date).find(p => p.type === 'timeZoneName')?.value ?? iana;
    return `${offset} ${name}`;
  }

  get filteredGroups(): { group: string; zones: string[] }[] {
    if (!this.tzSearch) return this.TIMEZONE_GROUPS;
    const q = this.tzSearch.toLowerCase();
    return this.TIMEZONE_GROUPS
      .map(g => ({ group: g.group, zones: g.zones.filter(z => this.tzDisplay(z).toLowerCase().includes(q)) }))
      .filter(g => g.zones.length > 0);
  }

  selectTimezone(iana: string) {
    this.timezone       = iana;
    this.tzDropdownOpen = false;
    this.tzSearch       = '';
    this.selectedDate   = null;
    this.selectedSlot   = null;
    this.slotsForDate   = [];
    const offset = this.currentMonthIndex - this.minMonthIndex;
    this.loadAvailability(offset);
  }

  private org    = Session.getOrg();
  private userId = Session.getUser().id;
  private user   = Session.getUser();

  // ── Day/month names ────────────────────────────────────────────────────────
  readonly DAY_LABELS  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  readonly MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  constructor(private signupService: SignupService) {}

  ngOnInit() {
    this.currentMonth = new Date();
    this.currentMonth.setDate(1);
    this.loadDurations();
  }

  // ── Availability (Angular → Java backend → HubSpot official API) ──────────

  // Step 1: probe with duration=0 to learn which durations HubSpot has configured,
  // then kick off the real slot fetch for the first available duration.
  loadDurations() {
    this.loading = true;
    this.error   = '';
    // Fetch configured durations from HubSpot meeting-link metadata (not slot data)
    // so we always get all durations regardless of current slot availability.
    this.signupService.getMeetingDurations(this.org, this.userId).subscribe({
      next: (res) => {
        const available = (res.availableDurations ?? []).sort((a, b) => a - b);
        this.durations        = available.map(ms => ({ ms, label: this.durationLabel(ms) }));
        this.selectedDuration = available[0] ?? 1_800_000;
        this.loadAvailability(0);
      },
      error: () => {
        this.error   = 'Could not load available times. Please try again.';
        this.loading = false;
        this.buildCalendar();
      },
    });
  }

  // Step 2: fetch slots for current month + next month in parallel.
  loadAvailability(monthOffset = 0) {
    this.loading = true;
    this.error   = '';

    forkJoin([
      this.signupService.getMeetingAvailability(this.org, this.userId, this.timezone, monthOffset, this.selectedDuration),
      this.signupService.getMeetingAvailability(this.org, this.userId, this.timezone, monthOffset + 1, this.selectedDuration),
    ]).subscribe({
      next: ([current, next]) => {
        this.slots = [...(current.slots ?? []), ...(next.slots ?? [])];
        this.groupSlotsByDate();
        this.buildCalendar();
        this.loading = false;
        const firstAvailable = this.calendarDays.find(d => d.hasSlots);
        if (firstAvailable) this.selectDate(firstAvailable);
      },
      error: () => {
        this.error   = 'Could not load available times. Please try again.';
        this.loading = false;
        this.buildCalendar();
      },
    });
  }

  private groupSlotsByDate() {
    this.slotsByDate = new Map();
    for (const slot of this.slots) {
      const key = this.toLocalDateKey(slot.startMillisUtc);
      if (!this.slotsByDate.has(key)) {
        this.slotsByDate.set(key, []);
      }
      this.slotsByDate.get(key)!.push(slot);
    }
  }

  // ── Calendar ───────────────────────────────────────────────────────────────

  buildCalendar() {
    const year  = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);

    // Start grid on Sunday of the week containing the 1st
    const startDay = new Date(firstDay);
    startDay.setDate(1 - firstDay.getDay());

    const lastDay = new Date(year, month + 1, 0); // last day of current month

    this.calendarDays = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDay);
      d.setDate(startDay.getDate() + i);

      // Stop once we've passed the last day of the current month
      if (d > lastDay) break;

      const isWeekend  = d.getDay() === 0 || d.getDay() === 6;
      const isPast     = d < this.today;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      this.calendarDays.push({
        date:           d,
        inCurrentMonth: d.getMonth() === month,
        isPrevMonth:    d.getMonth() !== month && d < firstDay,
        hasSlots:       !isPast && !isWeekend && this.slotsByDate.has(key),
        isToday:        d.getTime() === this.today.getTime(),
        isPast:         isPast || isWeekend,
      });
    }
  }

  get currentMonthLabel(): string {
    return `${this.MONTH_NAMES[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }

  prevMonth() {
    if (!this.canGoPrev) return;
    const d = new Date(this.currentMonth);
    d.setMonth(d.getMonth() - 1);
    this.currentMonth = d;
    const offset = this.currentMonthIndex - this.minMonthIndex;
    this.loadAvailability(offset);
  }

  nextMonth() {
    if (!this.canGoNext) return;
    const d = new Date(this.currentMonth);
    d.setMonth(d.getMonth() + 1);
    this.currentMonth = d;
    const offset = this.currentMonthIndex - this.minMonthIndex;
    this.loadAvailability(offset);
  }

  // ── Duration selection ─────────────────────────────────────────────────────

  selectDuration(ms: number) {
    if (ms === this.selectedDuration) return;
    this.selectedDuration = ms;
    this.selectedDate     = null;
    this.selectedSlot     = null;
    this.slotsForDate     = [];
    this.view             = 'calendar';
    const offset = this.currentMonthIndex - this.minMonthIndex;
    this.loadAvailability(offset);
  }

  // ── Date / slot selection ──────────────────────────────────────────────────

  selectDate(day: CalendarDay) {
    if (day.isPast || !day.hasSlots) return;
    this.selectedDate = day.date;
    this.selectedSlot = null;
    const key         = this.toLocalDateKey(day.date.getTime());
    this.slotsForDate = this.slotsByDate.get(key) || [];
    this.view         = 'slots';
  }

  selectSlot(slot: MeetingSlot) {
    this.selectedSlot = slot;
  }

  backToCalendar() {
    this.view         = 'calendar';
    this.selectedSlot = null;
  }

  // ── Booking (browser → our backend → HubSpot) ─────────────────────────────

  confirmBooking() {
    if (!this.selectedSlot) return;
    this.booking = true;
    this.error   = '';

    this.signupService.bookMeeting(this.org, this.userId, {
      firstName: this.user.first_name || '',
      lastName:  this.user.last_name  || '',
      email:     this.user.id         || '',
      startTime:  this.selectedSlot.startMillisUtc,
      timezone:   this.timezone,
      durationMs: this.selectedDuration,
    }).subscribe({
      next: (conf) => {
        this.confirmation = conf;
        this.booking      = false;
        this.view         = 'confirmed';
        this.booked.emit();
      },
      error: (err) => {
        this.booking = false;
        // handleError in SignupService unwraps HttpErrorResponse to its body,
        // so err = { error: "..." } and err.error is already the message string.
        const msg = (err?.error ?? err?.message ?? '') as string;
        if (msg.includes('SLOT_CONFLICT')) {
          this.error = 'That time slot was just taken. Please choose another.';
          this.selectedSlot = null;
        } else {
          this.error = 'Booking failed. Please try again.';
        }
      },
    });
  }

  // ── Formatting helpers ─────────────────────────────────────────────────────

  formatSlotTime(slot: MeetingSlot): string {
    const fmt = (ms: number) =>
      new Intl.DateTimeFormat('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZone: this.timezone,
      }).format(new Date(ms));
    return `${fmt(slot.startMillisUtc)} – ${fmt(slot.endMillisUtc)}`;
  }

  formatSelectedDate(): string {
    if (!this.selectedDate) return '';
    return this.selectedDate.toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  }

  private toLocalDateKey(ms: number): string {
    // Returns "YYYY-MM-DD" in the user's local timezone
    return new Intl.DateTimeFormat('en-CA', { timeZone: this.timezone }).format(new Date(ms));
  }

}
